import React from 'react';
import { Check, Plus, Smartphone, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import BirthdayPickerEn from '../../components/ui/BirthdayPickerEn';
import { CUSTOMER_NATIONALITY_OPTIONS } from '../../constants/customerNationalityOptions';
import {
  addCustomerProfilePhoneNumber,
  fetchCustomerProfile,
  updateCustomerProfile,
  type CustomerProfile,
} from '../../utils/profile/customerProfile';
import SubpageHeader from './SubpageHeader';

type Gender = 'male' | 'female' | 'other' | '';

type FormState = {
  last_name: string;
  first_name: string;
  gender: Gender;
  birthday: string;
  nationality: string;
  province: string;
  district: string;
  area: string;
};

const PROVINCES = [
  'Phnom Penh',
  'Banteay Meanchey',
  'Battambang',
  'Kampong Cham',
  'Kampong Chhnang',
  'Kampong Speu',
  'Kampong Thom',
  'Kampot',
  'Kandal',
  'Koh Kong',
  'Kratie',
  'Mondulkiri',
  'Oddar Meanchey',
  'Pailin',
  'Preah Sihanouk',
  'Preah Vihear',
  'Prey Veng',
  'Pursat',
  'Ratanakiri',
  'Siem Reap',
  'Stung Treng',
  'Svay Rieng',
  'Takeo',
  'Tbong Khmum',
];

const PHNOM_PENH_DISTRICTS = [
  '7 Makara',
  'Boeng Keng Kang',
  'Daun Penh',
  'Chbar Ampov',
  'Chamkar Mon',
  'Chroy Changvar',
  'Dangkao',
  'Mean Chey',
  'Prampi Makara',
  'Por Senchey',
  'Russey Keo',
  'Sen Sok',
  'Tuol Kouk',
];

const emptyForm: FormState = {
  last_name: '',
  first_name: '',
  gender: '',
  birthday: '',
  nationality: '',
  province: 'Phnom Penh',
  district: '',
  area: '',
};

const toForm = (profile: CustomerProfile): FormState => ({
  last_name: profile.last_name ?? '',
  first_name: profile.first_name ?? '',
  gender: profile.gender ?? '',
  birthday: profile.birthday ?? '',
  nationality: profile.nationality ?? '',
  province: profile.province ?? 'Phnom Penh',
  district: profile.district ?? '',
  area: profile.area ?? '',
});

const fieldClass =
  'w-full rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100';
const sectionClass = 'rounded-[26px] border border-slate-200 bg-white p-4 shadow-[0_16px_36px_rgba(15,23,42,0.06)]';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [addingPhone, setAddingPhone] = React.useState(false);
  const [profile, setProfile] = React.useState<CustomerProfile | null>(null);
  const [form, setForm] = React.useState<FormState>(emptyForm);
  const [newPhoneNumber, setNewPhoneNumber] = React.useState('');

  const loadProfile = React.useCallback(async () => {
    setLoading(true);
    try {
      const nextProfile = await fetchCustomerProfile();
      setProfile(nextProfile);
      setForm(toForm(nextProfile));
    } catch (error) {
      console.error('Failed to load customer profile:', error);
      toast.error('Failed to load profile.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const updateField = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.last_name.trim() || !form.first_name.trim()) {
      toast.warning('Please enter your family name and given name.');
      return;
    }

    setSaving(true);
    try {
      const nextProfile = await updateCustomerProfile({
        last_name: form.last_name.trim(),
        first_name: form.first_name.trim(),
        gender: form.gender,
        birthday: form.birthday || null,
        nationality: form.nationality || null,
        province: form.province || null,
        district: form.province === 'Phnom Penh' ? form.district || null : null,
        area: form.province === 'Phnom Penh' ? form.area || null : null,
      });
      setProfile(nextProfile);
      setForm(toForm(nextProfile));
      localStorage.setItem('customerName', `${nextProfile.last_name ?? ''} ${nextProfile.first_name ?? ''}`.trim());
      toast.success('Profile updated.');
    } catch (error: any) {
      console.error('Failed to save customer profile:', error);
      toast.error(error?.response?.data?.error ?? 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPhone = async () => {
    const phoneNumber = newPhoneNumber.trim();
    if (!phoneNumber) {
      toast.warning('Please enter a phone number.');
      return;
    }

    setAddingPhone(true);
    try {
      const nextProfile = await addCustomerProfilePhoneNumber(phoneNumber);
      setProfile(nextProfile);
      setForm(toForm(nextProfile));
      setNewPhoneNumber('');
      toast.success('Phone number added.');
    } catch (error: any) {
      console.error('Failed to add phone number:', error);
      const status = error?.response?.status;
      toast.error(
        status === 409
          ? 'This phone number is already used by another customer.'
          : error?.response?.data?.error ?? 'Failed to add phone number.'
      );
    } finally {
      setAddingPhone(false);
    }
  };

  const isPhnomPenh = form.province === 'Phnom Penh';

  return (
    <div className="space-y-4">
      <SubpageHeader title="Profile" onBack={() => navigate('/reserve/mypage')} />

      {loading ? (
        <div className={sectionClass}>
          <div className="text-sm text-slate-500">Loading profile...</div>
        </div>
      ) : (
        <>
          <section className={sectionClass}>
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <UserRound className="h-4 w-4 text-emerald-700" />
              Personal Information
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Name
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    className={fieldClass}
                    value={form.last_name}
                    onChange={(e) => updateField('last_name', e.target.value)}
                    placeholder="Family name"
                  />
                  <input
                    className={fieldClass}
                    value={form.first_name}
                    onChange={(e) => updateField('first_name', e.target.value)}
                    placeholder="Given name"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Gender
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['male', 'female', 'other'] as const).map((gender) => {
                    const active = form.gender === gender;
                    return (
                      <button
                        key={gender}
                        type="button"
                        onClick={() => updateField('gender', active ? '' : gender)}
                        className={[
                          'rounded-[16px] border px-2 py-2 text-sm font-semibold transition',
                          active
                            ? 'border-emerald-600 bg-emerald-600 text-white'
                            : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100',
                        ].join(' ')}
                      >
                        {gender.charAt(0).toUpperCase() + gender.slice(1)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <BirthdayPickerEn
                label="Birthday"
                value={form.birthday}
                onChange={(value) => updateField('birthday', value)}
                min="1900-01-01"
                max={new Date().toISOString().slice(0, 10)}
              />

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Nationality
                </label>
                <select
                  className={fieldClass}
                  value={form.nationality}
                  onChange={(e) => updateField('nationality', e.target.value)}
                >
                  <option value="">Not selected</option>
                  {CUSTOMER_NATIONALITY_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Location
                </label>
                <div className="space-y-3">
                  <select
                    className={fieldClass}
                    value={form.province}
                    onChange={(e) => {
                      updateField('province', e.target.value);
                      if (e.target.value !== 'Phnom Penh') {
                        setForm((prev) => ({ ...prev, district: '', area: '' }));
                      }
                    }}
                  >
                    <option value="">Not selected</option>
                    {PROVINCES.map((province) => (
                      <option key={province} value={province}>{province}</option>
                    ))}
                  </select>

                  {isPhnomPenh && (
                    <>
                      <select
                        className={fieldClass}
                        value={form.district}
                        onChange={(e) => updateField('district', e.target.value)}
                      >
                        <option value="">District</option>
                        {PHNOM_PENH_DISTRICTS.map((district) => (
                          <option key={district} value={district}>{district}</option>
                        ))}
                      </select>
                      <input
                        className={fieldClass}
                        value={form.area}
                        onChange={(e) => updateField('area', e.target.value)}
                        placeholder="Area"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-[20px] bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-60"
            >
              <Check className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </section>

          <section className={sectionClass}>
            <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Smartphone className="h-4 w-4 text-emerald-700" />
              Phone Numbers
            </div>

            <div className="space-y-2">
              {(profile?.phone_numbers ?? []).map((phone) => (
                <div
                  key={phone.id}
                  className="flex items-center justify-between gap-3 rounded-[18px] border border-slate-200 bg-slate-50 px-3 py-3"
                >
                  <div>
                    <div className="font-mono text-sm font-semibold text-slate-900">{phone.phone_number}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
                      {phone.is_primary && (
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">
                          Primary
                        </span>
                      )}
                      <span
                        className={[
                          'rounded-full border px-2 py-0.5',
                          phone.login_enabled
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 bg-white text-slate-500',
                        ].join(' ')}
                      >
                        {phone.login_enabled ? 'Login enabled' : 'Login not enabled'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              <input
                type="tel"
                className={fieldClass}
                value={newPhoneNumber}
                onChange={(e) => setNewPhoneNumber(e.target.value)}
                placeholder="Add phone number"
              />
              <button
                type="button"
                onClick={handleAddPhone}
                disabled={addingPhone}
                className="inline-flex h-[46px] w-[52px] shrink-0 items-center justify-center rounded-[18px] bg-slate-900 text-white transition hover:bg-slate-800 disabled:opacity-60"
                aria-label="Add phone number"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default ProfilePage;
